import './index.css'
import './i18n'

import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter as Router, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

import App from './App'
import environment from './environment'
import { queryClient } from './lib/queryClient'
// import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import store from './store/store'

if (!environment.isDev) {
  Sentry.init({
    dsn: 'https://676ec9f23a61962b3daa037e133169a0@o4508680792702976.ingest.de.sentry.io/4510097213816912',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    environment: environment.isProd ? 'production' : environment.isDev ? 'development' : 'staging',
    sendDefaultPii: true,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Error-only replays — org quota is 50/month; random session sampling burns it too fast
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
      Sentry.consoleLoggingIntegration({ levels: ['error', 'info'] }),
    ],
    // Derive from apiBaseUrl so staging/prod always match the live API host
    tracePropagationTargets: ['localhost', environment.apiBaseUrl].filter(Boolean),
    tracesSampleRate: 1.0,
    // Session Replay — only record sessions that hit an error (keeps quota usable)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Enable logs to be sent to Sentry
    enableLogs: true,
    enabled: true,
  })
}
//service-worker unregister
if (window.navigator && navigator.serviceWorker) {
  navigator.serviceWorker.getRegistrations().then(function (registrations) {
    for (const registration of registrations) {
      registration.unregister()
    }
  })
}

createRoot(document.getElementById('root')!).render(
  <Sentry.ErrorBoundary
    fallback={
      <div className='flex min-h-[100dvh] flex-col items-center justify-center gap-3 p-6 text-center'>
        <p className='text-lg font-medium'>Something went wrong</p>
        <button type='button' className='underline' onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    }
  >
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Router>
          <App />
        </Router>
      </Provider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
)
